import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Message } from '@twilio/conversations/lib/message';
import { Button, Grid } from '@chakra-ui/react';

interface Props {
  messages: Message[];
  children: ReactNode;
}

/*
 * This component is a scrollable container that wraps around the 'MessageList' component.
 * The MessageList will ultimately grow taller than its container as it continues to receive
 * new messages, and users will need to have the ability to scroll up and down the chat window.
 * A "new message" button will be displayed when the user receives a new message, and is not scrolled
 * to the bottom. This button will be hidden if the user clicks on it, or manually scrolls
 * to the bottom. Otherwise, this component will auto-scroll to the bottom when a new message is
 * received, and the user is already scrolled to the bottom.
 *
 * Note that this component is tested with Cypress only.
 */
const Container: React.FC<Props> = ({ messages, children }: Props) => {
  const chatThreadRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const [showButton, setShowButton] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [messageNotificationCount, setMessageNotificationCount] = useState(0);

  const scrollToBottom = (): void => {
    const innerScrollContainerEl = chatThreadRef.current!;
    innerScrollContainerEl.scrollTop = innerScrollContainerEl!.scrollHeight;

    setMessageCount(messages.length);
  };

  const handleScroll = (): void => {
    const innerScrollContainerEl = chatThreadRef.current!;
    // Because this.handleScroll() is a throttled method,
    // it's possible that it can be called after this component unmounts, and this element will be null.
    // Therefore, if it doesn't exist, don't do anything:
    if (!innerScrollContainerEl) return;

    // On systems using display scaling, scrollTop may return a decimal value, so we need to account for this in the
    // "isScrolledToBottom" calculation.
    const isAtBottom =
      Math.abs(
        innerScrollContainerEl.clientHeight +
        innerScrollContainerEl.scrollTop -
        innerScrollContainerEl!.scrollHeight
      ) < 1;

    setIsScrolledToBottom(isAtBottom);
    setShowButton((showButton) => (isAtBottom ? false : showButton));
  };

  const handleClick = (): void => {
    const innerScrollContainerEl = chatThreadRef.current!;

    innerScrollContainerEl.scrollTo({
      top: innerScrollContainerEl.scrollHeight,
      behavior: 'smooth',
    });

    setShowButton(false);
  };

  useEffect(() => {
    scrollToBottom();
  }, []);

  // This component updates as users send new messages:
  useEffect(() => {
    const newMessageCount = messages.length - messageCount;

    if (isScrolledToBottom && newMessageCount > 0) {
      scrollToBottom();
    } else if (newMessageCount > 0) {
      setShowButton(!isScrolledToBottom);
      setMessageNotificationCount(showButton ? newMessageCount : 1);
    }
  }, [messages]);

  return (
    <>
      <Grid
        alignContent="start"
        gap={2}
        minHeight="0"
        onScroll={handleScroll}
        overflowY="auto"
        paddingRight="0.5rem"
        position="relative"
        ref={chatThreadRef}
        data-cy-message-list-inner-scroll
      >
        {children}
      </Grid>
      <Button
        bottom="14px"
        hidden={!showButton}
        justifySelf="center"
        marginTop="-2rem"
        onClick={handleClick}
        position="relative"
        size="sm"
        variant="blue"
        zIndex="overlay"
        data-cy-new-message-button
      >
        {messageNotificationCount} new message
        {messageNotificationCount > 1 && 's'}
      </Button>
    </>
  );
};

export default Container;
